import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  DEFAULT_LOCATION, MAX_RECORD_SECONDS,
  selectDurationBracket, saveDraft, clearDraft,
} from "../../../lib/drafts";

// Preferred MIME types in descending order of size/quality tradeoff.
// VP9 → VP8 → generic WebM → MP4 (Safari). Chromium picks the first
// supported one; Safari almost always lands on video/mp4.
const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function _pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

/**
 * useMediaRecorder — bundles the MediaRecorder lifecycle, ondataavailable
 * batching, 1-sec timeslice batching, 3-min cap auto-stop, draft persistence
 * on stop, and the start/pause/resume/stop/retake handlers.
 *
 * Lives at /app/frontend/src/components/dashboard/recorder/useMediaRecorder.js.
 *
 * ⚠️ This hook is gated by the Playwright recorder.spec.js E2E harness.
 * Run `yarn test:e2e recorder` BEFORE AND AFTER any change to this file.
 * The 3 tests there record a real 4-second clip from Chromium's fake camera
 * and prove the lifecycle survives.
 *
 * Inputs:
 *   mediaStreamRef — MediaStream from getUserMedia (owned by camera lifecycle in parent).
 *                    Set to null after recording stops because we tear down the tracks
 *                    inside the onstop handler.
 *   liveVideoRef   — DOM ref to the live preview <video>. Cleared on stop so the
 *                    preview frame doesn't linger after the user stops recording.
 *   getLocation    — () => location, called on stop to snapshot the EXIF-like data
 *                    embedded in the draft. Parent passes a getter (not a value)
 *                    because location can be set async during the recording.
 *   onStop         — ({ blob, durationSeconds }) => void, fires AFTER state has
 *                    flipped to "review". Parent uses this to: setCameraReady(false),
 *                    setVideoDuration(durationSeconds → bracket).
 *   onRetake       — () => Promise<void>, fires when user taps Retake. Parent
 *                    re-acquires the camera + resets parent-owned form state.
 *
 * Returns:
 *   { recordPhase, setRecordPhase, isRecording, isPaused, recordedBlob,
 *     setRecordedBlob, recordedUrl, setRecordedUrl, recordTimer, setRecordTimer,
 *     recordStartedAt, setRecordStartedAt, recordTimerRef,
 *     startRecording, pauseRecording, resumeRecording, stopRecording, retakeRecording }
 *
 * Why expose setters: parent's `hydrateFromDraft` path injects a saved blob
 * directly into `recordedBlob` + `recordedUrl` without going through the
 * MediaRecorder. Setters let the parent skip the live-record pipeline when
 * resuming a draft from IndexedDB.
 */
export function useMediaRecorder({ mediaStreamRef, liveVideoRef, getLocation, onStop, onRetake }) {
  const [recordPhase, setRecordPhase] = useState("setup"); // 'setup' | 'review'
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordTimer, setRecordTimer] = useState(0);
  const [recordStartedAt, setRecordStartedAt] = useState(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordTimerIntervalRef = useRef(null);
  const recordTimerRef = useRef(0);

  const startRecording = () => {
    if (!mediaStreamRef.current) { toast.error("Camera not ready yet"); return; }
    const chosen = _pickMimeType();
    let recorder;
    try {
      recorder = chosen
        ? new MediaRecorder(mediaStreamRef.current, { mimeType: chosen })
        : new MediaRecorder(mediaStreamRef.current);
    } catch (_e) {
      toast.error("Recording isn't supported on this browser.");
      return;
    }
    recordedChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      // Tear down the camera tracks now that we have a recording — leaving
      // them live would keep the OS camera indicator on (iOS UX nit).
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (liveVideoRef.current) {
        try { liveVideoRef.current.srcObject = null; } catch (_e) { /* ignore */ }
      }

      const type = recorder.mimeType || "video/webm";
      const blob = new Blob(recordedChunksRef.current, { type });
      const durationSeconds = recordTimerRef.current || recordTimer;
      // Always fire onStop so the parent can sync its own camera state
      // (setCameraReady(false), commit duration bracket). Parent gets
      // a null blob on the empty-data error path.
      if (recordTimerIntervalRef.current) {
        clearInterval(recordTimerIntervalRef.current);
        recordTimerIntervalRef.current = null;
      }
      setIsRecording(false);
      setIsPaused(false);
      if (blob.size === 0) {
        // Edge case: user hit Stop before any chunk landed. Don't flip
        // into review phase — just leave the parent to recover.
        toast.error("Couldn't capture any video data — please try again.");
        onStop?.({ blob: null, durationSeconds });
        return;
      }
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setRecordedUrl(url);
      setRecordPhase("review");
      // Save the draft to IndexedDB so a navigation away doesn't lose it.
      try {
        await saveDraft({
          blob,
          startedAt: recordStartedAt || Date.now(),
          recordedSeconds: durationSeconds,
          location: (getLocation && getLocation()) || DEFAULT_LOCATION,
        });
      } catch (_err) { /* ignore */ }
      onStop?.({ blob, durationSeconds });
    };
    mediaRecorderRef.current = recorder;
    // Timeslice = 1000ms so ondataavailable fires every second instead of
    // only on stop — gives us a chance to bail (and keep partial data)
    // if the user closes the tab mid-record.
    recorder.start(1000);
    setIsRecording(true);
    setIsPaused(false);
    setRecordTimer(0);
    recordTimerRef.current = 0;
    setRecordStartedAt(Date.now());
    recordTimerIntervalRef.current = setInterval(() => {
      if (mediaRecorderRef.current?.state === "recording") {
        recordTimerRef.current = (recordTimerRef.current || 0) + 1;
        setRecordTimer(recordTimerRef.current);
        if (recordTimerRef.current >= MAX_RECORD_SECONDS) {
          try { mediaRecorderRef.current.stop(); } catch (_e) { /* ignore */ }
          toast.info("Hit the 3-minute cap — stopped automatically.");
        }
      }
    }, 1000);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.pause(); setIsPaused(true); } catch (_e) { /* ignore */ }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      try { mediaRecorderRef.current.resume(); setIsPaused(false); } catch (_e) { /* ignore */ }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_e) { /* ignore */ }
    }
  };

  const discardRecording = async () => {
    if (recordedUrl) {
      try { URL.revokeObjectURL(recordedUrl); } catch (_e) { /* ignore */ }
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordTimer(0);
    recordTimerRef.current = 0;
    setRecordStartedAt(null);
    setRecordPhase("setup");
    await clearDraft();
    // Parent re-acquires the camera (mediaStreamRef is now null because
    // onstop tore down the tracks).
    if (onRetake) await onRetake();
  };

  // retakeRecording is the public alias the controls bar calls.
  const retakeRecording = () => discardRecording();

  return {
    recordPhase, setRecordPhase,
    isRecording, isPaused,
    recordedBlob, setRecordedBlob,
    recordedUrl, setRecordedUrl,
    recordTimer, setRecordTimer,
    recordStartedAt, setRecordStartedAt,
    recordTimerRef,
    startRecording, pauseRecording, resumeRecording, stopRecording,
    retakeRecording, discardRecording,
  };
}

// Lint-friendly export of the constant for tests / debugging.
export const _MIME_CANDIDATES = MIME_CANDIDATES;
